import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { resolveAccessRequestErrorMessage } from "../lib/accessRequestErrors";
import { apiHelpers } from "../lib/api";
import type { CreateAccessRequestBody } from "../lib/accessRequests";
import {
  fetchAllAccessRequests,
  fetchPendingAccessRequestCount,
  isHouseholdApprover,
} from "../lib/accessRequests";
import { useAuthStore } from "../stores/authStore";

export const accessRequestQueryKeys = {
  all: ["access-requests"] as const,
  pendingCount: ["access-requests", "pending-count"] as const,
  mine: (scope: "self" | "household-mine") =>
    ["access-requests", "mine", scope] as const,
  pendingQueue: ["access-requests", "pending-queue"] as const,
};

function invalidateAccessQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: accessRequestQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: accessRequestQueryKeys.pendingCount });
  queryClient.invalidateQueries({ queryKey: ["access-requests", "mine"] });
  queryClient.invalidateQueries({ queryKey: accessRequestQueryKeys.pendingQueue });
  queryClient.invalidateQueries({ queryKey: ["home-dashboard"] });
  queryClient.invalidateQueries({ queryKey: ["apartments-dashboard"] });
  queryClient.invalidateQueries({ queryKey: ["payments-summary"] });
  queryClient.invalidateQueries({ queryKey: ["hoa-charges-summary"] });
}

export function usePendingAccessRequestCount() {
  const user = useAuthStore((s) => s.user);
  const isApprover = isHouseholdApprover(user?.role);

  return useQuery({
    queryKey: accessRequestQueryKeys.pendingCount,
    enabled: Boolean(user && isApprover),
    queryFn: () => fetchPendingAccessRequestCount(apiHelpers.accessRequests.list),
    staleTime: 30_000,
  });
}

export function usePendingAccessRequestQueue() {
  const user = useAuthStore((s) => s.user);
  const isApprover = isHouseholdApprover(user?.role);

  return useQuery({
    queryKey: accessRequestQueryKeys.pendingQueue,
    enabled: Boolean(user && isApprover),
    queryFn: () =>
      fetchAllAccessRequests(apiHelpers.accessRequests.list, { status: "pending" }),
    staleTime: 30_000,
  });
}

export function useAccessRequestMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (body: CreateAccessRequestBody) => apiHelpers.accessRequests.create(body),
    onSuccess: () => {
      invalidateAccessQueries(queryClient);
      toast.success(t("accessRequests.toast.created"));
    },
    onError: (error: unknown) => {
      const message =
        resolveAccessRequestErrorMessage(t, error, "create") ??
        t("accessRequests.toast.createError");
      toast.error(message);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => apiHelpers.accessRequests.cancel(id),
    onSuccess: () => {
      invalidateAccessQueries(queryClient);
      toast.success(t("accessRequests.toast.cancelled"));
    },
    onError: (error: unknown) => {
      const message =
        resolveAccessRequestErrorMessage(t, error, "cancel") ??
        t("accessRequests.toast.cancelError");
      toast.error(message);
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => apiHelpers.accessRequests.approve(id),
    onSuccess: async () => {
      invalidateAccessQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: ["users", "me"] });
      toast.success(t("accessRequests.toast.approved"));
    },
    onError: (error: unknown) => {
      const message =
        resolveAccessRequestErrorMessage(t, error, "approve") ??
        t("accessRequests.toast.decideError");
      toast.error(message);
    },
  });

  const denyMutation = useMutation({
    mutationFn: (id: number) => apiHelpers.accessRequests.deny(id),
    onSuccess: () => {
      invalidateAccessQueries(queryClient);
      toast.success(t("accessRequests.toast.denied"));
    },
    onError: (error: unknown) => {
      const message =
        resolveAccessRequestErrorMessage(t, error, "deny") ??
        t("accessRequests.toast.decideError");
      toast.error(message);
    },
  });

  return {
    createMutation,
    cancelMutation,
    approveMutation,
    denyMutation,
  };
}
